import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowEnvVars
} from '../types'
import { Workflow } from '../workflow'
import { DataWarehouseConnection } from '../workflow/connections/index'
import { FilterWorkflowComponent } from './common/filtercomponent'

const predicates = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['within', 'ST_WITHIN'],
  ['covers', 'ST_COVERS']
])

class SpatialFilter extends FilterWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialfilter',
        group: 'Data Preparation',
        title: 'Spatial Filter',
        description: `
      This component filters an input table using a spatial predicate and a filter table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'filter',
          type: WorkflowComponentParamType.InputTable,
          title: 'Filter table'
        },
        {
          name: 'geosource',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in source table',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geofilter',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in filter table',
          parent: 'filter',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'predicate',
          type: WorkflowComponentParamType.Selection,
          title: 'Spatial predicate',
          default: predicates.keys().next().value,
          optional: true,
          options: Array.from(predicates.keys())
        }
      ]
    )
  }

  async _getUnmatchCode(
    params: Record<string, unknown>,
    matchTablename: string,
    envVars: WorkflowEnvVars,
    workflow: Workflow,
    componentName: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schema?: TableSchema
  ) {
    matchTablename = connection.quoteName(matchTablename)
    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake:
      case Provider.Redshift:
      case Provider.Postgres:
        return dedent`
            SELECT
              __source.*
            FROM
              ${connection.quoteName(params.source as string)} __source
            WHERE ST_ASBINARY(${params.geosource}) NOT IN
              (
                SELECT ST_ASBINARY(${params.geosource}) AS ${params.geosource}
                FROM ${matchTablename}
              )`
      case Provider.Oracle:
        return dedent`
            SELECT
              "__source".*
            FROM
              ${connection.quoteName(params.source as string)} "__source",
              ${connection.quoteName(params.filter as string)} "__filter"
            WHERE SDO_RELATE(
              "__filter".${params.geofilter},
              "__source".${params.geosource},
              'mask=ANYINTERACT'
            ) = 'FALSE'`
      default:
        return null
    }
  }

  async _getMatchCode(
    params: Record<string, unknown>,
    envVars: WorkflowEnvVars,
    workflow: Workflow,
    componentName: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schema?: TableSchema
  ) {
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`
          WITH withids AS (
            SELECT
              __source.*,
              ROW_NUMBER()
                OVER()
                __id
            FROM
              ${connection.quoteName(params.source as string)} __source
          ),
          repeated AS (
            SELECT
              __source.*
            FROM
              withids __source,
              ${connection.quoteName(params.filter as string)} __filter
            WHERE ${predicates.get(params.predicate as string)}(
              __filter.${params.geofilter}, __source.${params.geosource}
            )
          )
          SELECT * EXCEPT(__id, __rn)
          FROM (
            SELECT
                *,
                ROW_NUMBER()
                    OVER (PARTITION BY __id)
                    __rn
            FROM repeated
          )
          WHERE __rn = 1
          `
      case Provider.Snowflake:
        return dedent`
          WITH withids AS (
            SELECT
              __source.*,
              ROW_NUMBER()
                OVER(ORDER BY 1)
                __id
            FROM
              ${connection.quoteName(params.source as string)} __source
          ),
          repeated AS (
            SELECT
              __source.*
            FROM
              withids __source,
              ${connection.quoteName(params.filter as string)} __filter
            WHERE ${predicates.get(params.predicate as string)}(
              __filter.${params.geofilter}, __source.${params.geosource}
            )
          )
          SELECT * EXCLUDE(__id, __rn)
          FROM (
            SELECT
                *,
                ROW_NUMBER()
                    OVER (PARTITION BY __id ORDER BY 1)
                    __rn
            FROM repeated
          )
          WHERE __rn = 1
          `
      case Provider.Redshift:
      case Provider.Postgres:
        return dedent`SELECT
                  __source.*
                FROM
                  ${connection.quoteName(params.source as string)} __source,
                  ${connection.quoteName(params.filter as string)} __filter
                WHERE ${predicates.get(params.predicate as string)}(
                  __filter.${params.geofilter}, __source.${params.geosource}
                )`
      case Provider.Oracle:
        return dedent`SELECT
              "__source".*
            FROM
              ${connection.quoteName(params.source as string)} "__source",
              ${connection.quoteName(params.filter as string)} "__filter"
            WHERE SDO_RELATE(
              "__filter".${params.geofilter},
              "__source".${params.geosource},
              'mask=ANYINTERACT'
            ) = 'TRUE'`
      default:
        return null
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.Redshift, Provider.Oracle]
  }
}

export { SpatialFilter }
